import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import debounce from "lodash/debounce";

const Step0UserSelection = ({ formData, updateFormData, nextStep, token }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [errors, setErrors] = useState({});

  // Debounced search function
  const searchUsers = useCallback(
    debounce(async (query) => {
      if (!query || query.length < 2) {
        setSearchResults([]);
        setShowResults(false);
        return;
      }

      setIsSearching(true);
      try {
        const config = {
          method: "get",
          url: `${process.env.REACT_APP_API}admin/search-users?q=${encodeURIComponent(query)}`,
          headers: {
            "x-access-token": token,
          },
        };

        const response = await axios(config);
        if (response.data.status === 200) {
          setSearchResults(response.data.data || []);
          setShowResults(true);
        }
      } catch (error) {
        console.error("Error searching users:", error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    [token]
  );

  useEffect(() => {
    searchUsers(searchQuery);
  }, [searchQuery, searchUsers]);

  const handleSelectUser = (user) => {
    updateFormData({
      user_id: user.id,
      user_email: user.email,
      first_name: user.firstName || "",
      last_name: user.lastName || "",
      isNewUser: false,
    });
    setShowResults(false);
    setSearchQuery("");
  };

  const handleCreateNewUser = () => {
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(searchQuery)) {
      setErrors({ email: "Please enter a valid email address" });
      return;
    }

    updateFormData({
      user_id: null,
      user_email: searchQuery,
      first_name: "",
      last_name: "",
      isNewUser: true,
    });
    setShowResults(false);
  };

  const handleClearSelection = () => {
    updateFormData({
      user_id: null,
      user_email: "",
      first_name: "",
      last_name: "",
      isNewUser: false,
    });
    setSearchQuery("");
    setErrors({});
  };

  const validateAndNext = () => {
    const newErrors = {};

    if (!formData.user_email) {
      newErrors.email = "Please select or enter a user email";
    }

    if (formData.isNewUser) {
      if (!formData.first_name?.trim()) {
        newErrors.first_name = "First name is required for new users";
      }
      if (!formData.last_name?.trim()) {
        newErrors.last_name = "Last name is required for new users";
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      nextStep();
    }
  };

  const hasSelectedUser = formData.user_email && (formData.user_id || formData.isNewUser);

  return (
    <div className="step-form">
      <div className="form-section">
        <h3 className="section-title">User Selection</h3>
        <p style={{ color: "#666", marginBottom: "20px" }}>
          Search for an existing user by email or name, or enter a new email to create a user account.
        </p>

        {!hasSelectedUser ? (
          <div className="form-group user-search">
            <label>
              Search User <span className="required">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter email or name to search..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setErrors({});
              }}
              onFocus={() => searchQuery.length >= 2 && setShowResults(true)}
            />
            {errors.email && <div className="error-message">{errors.email}</div>}

            {showResults && (
              <div className="search-results">
                {isSearching ? (
                  <div className="no-results">Searching...</div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((user) => (
                    <div
                      key={user.id}
                      className="search-result-item"
                      onClick={() => handleSelectUser(user)}
                    >
                      <div className="user-info">
                        <div className="user-name">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="user-email">{user.email}</div>
                      </div>
                      <span className={`user-status ${user.userStatus === 1 ? "active" : "inactive"}`}>
                        {user.userStatus === 1 ? "Active" : "Inactive"}
                      </span>
                    </div>
                  ))
                ) : searchQuery.length >= 2 ? (
                  <div className="no-results">
                    <p>No users found matching "{searchQuery}"</p>
                    {searchQuery.includes("@") && (
                      <button className="create-new-btn" onClick={handleCreateNewUser}>
                        Create new user with this email
                      </button>
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        ) : (
          <div className="selected-user">
            <div className="user-details">
              <div className="user-name">
                {formData.isNewUser
                  ? `${formData.first_name || "New"} ${formData.last_name || "User"}`
                  : `${formData.first_name} ${formData.last_name}`}
              </div>
              <div className="user-email">{formData.user_email}</div>
              <span className={`user-badge ${formData.isNewUser ? "new" : "existing"}`}>
                {formData.isNewUser ? "New User" : "Existing User"}
              </span>
            </div>
            <button className="clear-btn" onClick={handleClearSelection}>
              Change User
            </button>
          </div>
        )}

        {/* New User Name Fields */}
        {formData.isNewUser && (
          <div className="form-row" style={{ marginTop: "20px" }}>
            <div className="form-group">
              <label>
                First Name <span className="required">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter first name"
                value={formData.first_name}
                onChange={(e) => {
                  updateFormData({ first_name: e.target.value });
                  setErrors((prev) => ({ ...prev, first_name: null }));
                }}
              />
              {errors.first_name && <div className="error-message">{errors.first_name}</div>}
            </div>
            <div className="form-group">
              <label>
                Last Name <span className="required">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter last name"
                value={formData.last_name}
                onChange={(e) => {
                  updateFormData({ last_name: e.target.value });
                  setErrors((prev) => ({ ...prev, last_name: null }));
                }}
              />
              {errors.last_name && <div className="error-message">{errors.last_name}</div>}
            </div>
          </div>
        )}
      </div>

      <div className="step-buttons">
        <div></div>
        <button className="btn btn-primary" onClick={validateAndNext} disabled={!formData.user_email}>
          Next: Business Info
        </button>
      </div>
    </div>
  );
};

export default Step0UserSelection;
